// ============================================================================
// Agentes de código — registro y presencia (#511).
// ============================================================================
// Los runners viven en la PC de alguien, así que la mesa no sabe si el trabajo
// de código se está atendiendo a menos que cada uno lo diga. Esta pantalla es
// el panel de ese registro, con el mismo patrón de los dispositivos de acceso
// en las apps de patio: punto verde / morado / gris y "última vez visto".
//
// Dos cosas que no son cosméticas:
//  • la llave se muestra UNA sola vez, al crearla, y no se puede recuperar
//    (el backend sólo guarda su sha256);
//  • cuando hay trabajo encolado y CERO agentes conectados, el panel lo grita:
//    es el hueco que el hospedaje local no puede evitar por sí solo.
// ============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { brand as themeBrand } from "../../../config/theme";
import {
  getServiceDeskAgents,
  createServiceDeskAgent,
  renameServiceDeskAgent,
  revokeServiceDeskAgent,
  formatDate,
  type AgentsPanel,
  type AgentPresence,
  type ServiceDeskAgent,
} from "../services/serviceDeskService";
import { DeskLoadErrorPanel, useDeskLoadError } from "../components/DeskLoadError";
import { apiErrorMessage } from "../services/envelope";

const PRESENCE_META: Record<AgentPresence, { label: string; color: string; bg: string }> = {
  online: { label: "En línea", color: "#059669", bg: "#d1fae5" },
  working: { label: "Trabajando", color: "#7c3aed", bg: "#ede9fe" },
  offline: { label: "Desconectado", color: "#6b7280", bg: "#f3f4f6" },
  revoked: { label: "Revocado", color: "#dc2626", bg: "#fee2e2" },
};

// El latido es cada ~30 s y la ventana de "en línea" son 2 min: con 60 s el
// punto sigue siendo honesto (se pierde a lo sumo un latido de margen) y la
// pestaña deja de pedir el barrido de la cola tres veces por minuto.
const REFRESH_MS = 60000;

// SweetAlert2 renderiza `title` y `html` como HTML (parseHtmlToContainer →
// DOMParser → appendChild), así que cualquier dato del servidor que se
// interpole ahí se ejecuta. `titleText`/`text` asignan innerText y son seguros;
// para lo que SÍ tiene que ir en `html` se escapa aquí. (El backend además
// guarda los nombres sin marcado: los dos lados, porque ni el saneo alcanza a
// los nombres ya guardados ni el escape sobrevive a un descuido futuro.)
const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
  );

export function PresenceBadge({ presence }: { presence: AgentPresence }) {
  const meta = PRESENCE_META[presence] || PRESENCE_META.offline;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{ color: meta.color, backgroundColor: meta.bg }}
    >
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.color }} />
      {meta.label}
    </span>
  );
}

export default function AgentsSection() {
  const [panel, setPanel] = useState<AgentsPanel | null>(null);
  const [busy, setBusy] = useState(false);
  const { error: loadError, capture } = useDeskLoadError();
  // Un fallo de refresco no debe borrar lo que ya se está viendo.
  const loadedOnce = useRef(false);

  const load = useCallback(async () => {
    const data = await getServiceDeskAgents();
    setPanel(data);
    loadedOnce.current = true;
  }, []);

  useEffect(() => {
    load().catch(capture);
    const timer = setInterval(() => {
      load().catch(() => {});
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load, capture]);

  const card = "bg-surface border-line";
  const text = "text-content";
  const muted = "text-content-muted";

  const onCreate = async () => {
    const { value } = await Swal.fire({
      title: "Nuevo agente de código",
      html:
        '<input id="ag-name" class="swal2-input" placeholder="Nombre (ej. Runner de la oficina)">' +
        '<input id="ag-host" class="swal2-input" placeholder="Equipo / host (opcional)">',
      preConfirm: () => ({
        name: (document.getElementById("ag-name") as HTMLInputElement)?.value?.trim(),
        host: (document.getElementById("ag-host") as HTMLInputElement)?.value?.trim(),
      }),
      showCancelButton: true,
      confirmButtonText: "Crear y generar llave",
      confirmButtonColor: themeBrand.primary,
    });
    if (!value?.name) return;

    setBusy(true);
    try {
      const created = await createServiceDeskAgent({ name: value.name, host: value.host || undefined });
      await load().catch(() => {});
      // La llave existe UNA vez. El diálogo lo dice sin rodeos y permite
      // copiarla; después de cerrarlo ya no hay forma de volver a verla.
      await Swal.fire({
        icon: "success",
        title: "Copia la llave ahora",
        html:
          '<p style="font-size:13px;margin-bottom:8px">Esta llave no se vuelve a mostrar. Guárdala en la configuración del runner.</p>' +
          `<code style="display:block;word-break:break-all;background:#f3f4f6;padding:10px;border-radius:8px;font-size:12px">${escapeHtml(created.key)}</code>`,
        showCancelButton: true,
        confirmButtonText: "Copiar llave",
        cancelButtonText: "Ya la guardé",
        confirmButtonColor: themeBrand.primary,
      }).then((r) => {
        if (r.isConfirmed) navigator.clipboard?.writeText(created.key);
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo crear el agente", text: apiErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const onRename = async (agent: ServiceDeskAgent) => {
    const { value } = await Swal.fire({
      title: "Renombrar agente",
      input: "text",
      inputValue: agent.name,
      showCancelButton: true,
      confirmButtonText: "Guardar",
      confirmButtonColor: themeBrand.primary,
    });
    if (!value || value === agent.name) return;
    try {
      await renameServiceDeskAgent(agent.id, value);
      await load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo renombrar", text: apiErrorMessage(err) });
    }
  };

  const onRevoke = async (agent: ServiceDeskAgent) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      // `titleText`, no `title`: el nombre lo eligió quien dio de alta el agente
      // y `title` se pinta como HTML.
      titleText: `¿Revocar «${agent.name}»?`,
      text: "Su llave deja de funcionar de inmediato y no se puede reactivar: habrá que crear un agente nuevo.",
      showCancelButton: true,
      confirmButtonText: "Revocar",
      confirmButtonColor: "#dc2626",
    });
    if (!isConfirmed) return;
    try {
      await revokeServiceDeskAgent(agent.id);
      await load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo revocar", text: apiErrorMessage(err) });
    }
  };

  if (loadError) return <DeskLoadErrorPanel error={loadError} />;
  if (!panel && !loadedOnce.current) return <div className="p-8 text-center text-gray-400">Cargando…</div>;
  if (!panel) return null;

  const { agents, presence, queue, warning } = panel;

  return (
    <div className="p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${text}`}>Agentes de código</h1>
          <p className={`text-xs ${muted}`}>
            Los runners que toman tickets, escriben código y abren Pull Requests. Aparecen en línea mientras
            mandan su latido (cada ~30 s); a los 2 minutos sin latir se marcan como desconectados.
          </p>
        </div>
        <button
          onClick={onCreate}
          disabled={busy}
          className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          style={{ backgroundColor: themeBrand.primary }}
        >
          + Dar de alta un agente
        </button>
      </div>

      <AgentQueueWarning warning={warning} queue={queue} />

      <div className="mb-5 grid gap-3 sm:grid-cols-4">
        <Stat label="Conectados" value={presence.live} color="#059669" card={card} text={text} muted={muted} />
        <Stat label="Trabajando" value={presence.working} color="#7c3aed" card={card} text={text} muted={muted} />
        <Stat label="Desconectados" value={presence.offline} color="#6b7280" card={card} text={text} muted={muted} />
        {/* El barrido de la cola está acotado a una página, así que `waiting` es
            un SUELO, no un total: con `capped` se pinta «25+» en vez de un
            número exacto que sería mentira. Y si el contador no se pudo
            calcular, se dice —un guion, no un cero que inventa calma.

            El «+» sólo se pinta si AÑADE algo. `capped` significa «la página
            volvió llena», no «hay más de los que ves»: una mesa con 25 tickets
            abiertos de los que ninguno es elegible pintaba «0+», un «al menos
            cero» que se lee como un número truncado. */}
        <Stat
          label="Tickets en cola"
          value={
            !queue.enabled || queue.unavailable
              ? "—"
              : `${queue.waiting}${queue.capped && queue.waiting > 0 ? "+" : ""}`
          }
          color={
            !queue.unavailable && queue.waiting > 0 && presence.live === 0 ? "#dc2626" : "#2563eb"
          }
          card={card}
          text={text}
          muted={muted}
        />
      </div>

      <div className={`rounded-xl border ${card}`}>
        {agents.length === 0 ? (
          <div className="p-8 text-center">
            <p className={`text-sm ${text}`}>Todavía no hay ningún agente dado de alta.</p>
            <p className={`mt-1 text-xs ${muted}`}>
              Mientras tanto, los runners que usan la llave global <code>SD_AGENT_KEY</code> siguen funcionando;
              simplemente no se pueden ver aquí en línea.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-dark-border">
            {agents.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-sm font-semibold ${text}`}>{a.name}</span>
                    <PresenceBadge presence={a.presence} />
                    {a.keyPrefix && (
                      <code className={`text-[11px] ${muted}`} title="Prefijo de la llave (nunca la llave completa)">
                        {a.keyPrefix}…
                      </code>
                    )}
                  </div>
                  <p className={`mt-0.5 text-xs ${muted}`}>
                    {a.host || "equipo sin reportar"}
                    {a.version ? ` · v${a.version}` : ""} · Última vez visto:{" "}
                    {a.lastSeenAt ? formatDate(a.lastSeenAt) : "nunca"}
                  </p>
                  {a.presence === "working" && a.currentJob && (
                    <p className="mt-0.5 text-xs font-medium text-purple-600">
                      {/* El registro es de la mesa entera, no por marca: a un
                          operador acotado el backend le quita la referencia del
                          ticket, y aquí se dice así en vez de mostrar un hueco. */}
                      {a.currentJob.reference || a.currentJob.ticketId
                        ? `Trabajando el ticket ${a.currentJob.reference || a.currentJob.ticketId}`
                        : "Trabajando un ticket fuera de tu alcance de marcas"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {a.presence !== "revoked" ? (
                    <>
                      <button onClick={() => onRename(a)} className={`text-xs ${muted} hover:underline`}>
                        Renombrar
                      </button>
                      <button onClick={() => onRevoke(a)} className="text-xs text-red-500 hover:underline">
                        Revocar
                      </button>
                    </>
                  ) : (
                    <span className={`text-xs ${muted}`}>Revocado {a.revokedAt ? formatDate(a.revokedAt) : ""}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * El aviso del hueco: hay trabajo de código esperando y nadie escuchando.
 * Se exporta para poder colgarlo también del tablero (DashboardSection).
 */
export function AgentQueueWarning({
  warning,
  queue,
}: {
  warning: AgentsPanel["warning"];
  queue?: AgentsPanel["queue"];
}) {
  if (!warning) return null;
  // La flota que todavía usa la llave global heredada no reporta presencia, así
  // que ahí "nadie en línea" no significa "nadie trabajando": el backend baja
  // el aviso a informativo y aquí se pinta ámbar, no rojo de alarma.
  const info = warning.level === "info";
  const tone = info
    ? {
        box: "border-amber-300 bg-amber-50 dark:bg-amber-500/10",
        icon: "ri-information-line text-amber-600",
        title: "text-amber-800 dark:text-amber-300",
        body: "text-amber-800/90 dark:text-amber-300/80",
        heading: "Ningún agente de código dado de alta",
      }
    : {
        box: "border-red-300 bg-red-50 dark:bg-red-500/10",
        icon: "ri-alarm-warning-line text-red-600",
        title: "text-red-700 dark:text-red-300",
        body: "text-red-700/90 dark:text-red-300/80",
        heading: "Ningún agente de código conectado",
      };
  return (
    <div role="alert" className={`mb-5 flex items-start gap-3 rounded-xl border p-4 ${tone.box}`}>
      <i className={`mt-0.5 text-lg ${tone.icon}`} aria-hidden />
      <div>
        <p className={`text-sm font-semibold ${tone.title}`}>{tone.heading}</p>
        <p className={`mt-0.5 text-xs ${tone.body}`}>{warning.message}</p>
        {queue?.oldestWaitingAt && (
          <p className={`mt-0.5 text-xs ${tone.body}`}>
            El más antiguo espera desde {formatDate(queue.oldestWaitingAt)}.
          </p>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  color,
  card,
  text,
  muted,
}: {
  label: string;
  value: number | string;
  color: string;
  card: string;
  text: string;
  muted: string;
}) {
  return (
    <div className={`rounded-xl border p-4 ${card}`}>
      <p className={`text-xs ${muted}`}>{label}</p>
      <p className={`text-2xl font-bold ${text}`} style={{ color }}>
        {value}
      </p>
    </div>
  );
}
